import React, { useState, useEffect, useCallback, useRef, memo } from 'react'
import { api, formatCurrency } from '../../utils/api'
import { applyColourAndRatio, removeBackground, urlToBase64, validateImageFile, checkImageQuality } from '../../utils/imageProcessing'
import { useAuth } from '../../hooks/useAuth'

// Design System (matching warehouse components)
const theme = {
  colors: {
    charcoal: '#222725',
    sunburst: '#FFD44D',
    greenOnions: '#3BB273',
    imBlue: '#4E8098',
    crimson: '#E5625E',
    porcelain: '#F2EDE4',
    white: '#FFFFFF',
    grey100: '#F8F9F8',
    grey200: '#E7E5E4',
    grey300: '#D6D3D1',
    grey400: '#A8A29E',
    grey500: '#78716C',
    greenLight: '#E8F5EE',
    amberLight: '#FFF9E6',
    redLight: '#FCEEED',
    purple: '#8B5CF6',
    purpleLight: '#F5F3FF',
    blueLight: '#EDF3F6'
  },
  fonts: { sans: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif" },
  radius: { sm: '6px', md: '10px', lg: '16px' },
  shadows: { sm: '0 1px 2px rgba(0,0,0,0.05)', md: '0 4px 12px rgba(0,0,0,0.08)' }
}

// Source labels and colours
const SOURCE_LABELS = {
  testing: 'Testing',
  issue_trades: 'Issue Trades',
  repairs: 'Repairs',
  manual_listing: 'Manual Listing'
}
const SOURCE_COLORS = {
  testing: '#FFD44D',
  issue_trades: '#E5625E',
  repairs: '#8B5CF6',
  manual_listing: '#4E8098'
}

const STATUS_LABELS = {
  pending: 'Pending',
  in_progress: 'In Progress',
  completed: 'Completed'
}
const STATUS_BADGE_COLORS = {
  pending: 'amber',
  in_progress: 'blue',
  completed: 'green'
}

// Reusable UI primitives (matching warehouse pattern)
const Card = ({ children, style = {}, padding = true }) => (
  <div style={{
    background: theme.colors.white,
    borderRadius: theme.radius.lg,
    boxShadow: theme.shadows.sm,
    border: `1px solid ${theme.colors.grey200}`,
    padding: padding ? '24px' : 0,
    ...style
  }}>
    {children}
  </div>
)

const Badge = ({ children, color = 'grey' }) => {
  const colors = {
    grey: { bg: theme.colors.grey200, text: theme.colors.grey500 },
    green: { bg: theme.colors.greenLight, text: theme.colors.greenOnions },
    amber: { bg: theme.colors.amberLight, text: '#B8860B' },
    red: { bg: theme.colors.redLight, text: theme.colors.crimson },
    purple: { bg: theme.colors.purpleLight, text: theme.colors.purple },
    blue: { bg: theme.colors.blueLight, text: theme.colors.imBlue }
  }
  const c = colors[color] || colors.grey
  return (
    <span style={{
      display: 'inline-flex',
      padding: '4px 10px',
      borderRadius: '20px',
      fontSize: '12px',
      fontWeight: 600,
      background: c.bg,
      color: c.text
    }}>
      {children}
    </span>
  )
}

const Button = ({ children, variant = 'primary', size = 'md', fullWidth = false, disabled = false, onClick, style = {} }) => {
  const variants = {
    primary: { background: theme.colors.sunburst, color: theme.colors.charcoal },
    secondary: { background: theme.colors.white, color: theme.colors.charcoal, border: `1px solid ${theme.colors.grey300}` },
    success: { background: theme.colors.greenOnions, color: theme.colors.white },
    danger: { background: theme.colors.crimson, color: theme.colors.white },
    ghost: { background: 'transparent', color: theme.colors.grey500 }
  }
  const sizes = {
    sm: { padding: '8px 14px', fontSize: '13px' },
    md: { padding: '12px 20px', fontSize: '14px' },
    lg: { padding: '16px 28px', fontSize: '16px' }
  }
  const v = variants[variant]
  const s = sizes[size]
  return (
    <button onClick={onClick} disabled={disabled} style={{
      ...v, ...s, ...style,
      border: v.border || 'none',
      borderRadius: theme.radius.md,
      fontWeight: 600,
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.5 : 1,
      width: fullWidth ? '100%' : 'auto',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px'
    }}>
      {children}
    </button>
  )
}

const Spinner = ({ size = 20 }) => (
  <div style={{
    width: size, height: size,
    border: `2px solid ${theme.colors.grey200}`,
    borderTopColor: theme.colors.charcoal,
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite'
  }}>
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
)

// --- Utility helpers ---

function formatTimeAgo(dateString) {
  if (!dateString) return '-'
  const now = new Date()
  const date = new Date(dateString)
  const diffMs = now - date
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  return `${diffDays}d ago`
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      const base64 = reader.result.split(',')[1]
      resolve({ base64, contentType: file.type })
    }
    reader.onerror = () => reject(new Error('Failed to read file'))
    reader.readAsDataURL(file)
  })
}

// --- Sub-components ---

function SourceBadge({ source }) {
  const label = SOURCE_LABELS[source] || source
  const color = SOURCE_COLORS[source] || theme.colors.grey400
  return (
    <span style={{
      display: 'inline-flex',
      padding: '4px 10px',
      borderRadius: '20px',
      fontSize: '12px',
      fontWeight: 600,
      background: `${color}20`,
      color: color
    }}>
      {label}
    </span>
  )
}

function ProgressBar({ current, total, label }) {
  const pct = total > 0 ? Math.round((current / total) * 100) : 0
  return (
    <div style={{ width: '100%' }}>
      {label && (
        <div style={{ fontSize: '13px', color: theme.colors.grey500, marginBottom: '6px' }}>
          {label} ({current}/{total})
        </div>
      )}
      <div style={{
        width: '100%',
        height: '8px',
        background: theme.colors.grey200,
        borderRadius: '4px',
        overflow: 'hidden'
      }}>
        <div style={{
          width: `${pct}%`,
          height: '100%',
          background: theme.colors.greenOnions,
          borderRadius: '4px',
          transition: 'width 0.3s ease'
        }} />
      </div>
    </div>
  )
}

function PhotoEditModal({ photo, itemId, onClose, onProcessed, onBgRemovalUsed }) {
  const [imageAdjustments, setImageAdjustments] = useState({ brightness: 100, contrast: 100, autoApplied: false })
  const [cropMode, setCropMode] = useState(false)
  const [cropArea, setCropArea] = useState({ x: 0, y: 0, width: 100, height: 100 })
  const [processedImage, setProcessedImage] = useState(null)
  const [removingBg, setRemovingBg] = useState(false)
  const [makingRatio, setMakingRatio] = useState(false)
  const [autoProcessing, setAutoProcessing] = useState(false)
  const [autoProcessStep, setAutoProcessStep] = useState('')
  const [bgModel, setBgModel] = useState('isnet-general-use')
  const [bgSource, setBgSource] = useState(null)
  const [bgCredits, setBgCredits] = useState(null)
  const [saving, setSaving] = useState(false)
  const [modalToast, setModalToast] = useState(null)

  const bgAlreadyUsed = !!photo?.bg_removal_used

  function showModalToast(message, type = 'error') {
    setModalToast({ message, type })
    setTimeout(() => setModalToast(null), 5000)
  }

  const imageRef = useRef(null)
  const cropStartRef = useRef(null)

  useEffect(() => {
    if (!photo) return
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') { handleClose() }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [photo])

  if (!photo) return null

  // --- Helpers ---

  function resetImageAdjustments() {
    setImageAdjustments({ brightness: 100, contrast: 100, autoApplied: false })
    setCropMode(false)
    setProcessedImage(null)
    setBgSource(null)
    setBgCredits(null)
  }

  function handleClose() {
    resetImageAdjustments()
    onClose()
  }

  function getImageFilter() {
    return `brightness(${imageAdjustments.brightness}%) contrast(${imageAdjustments.contrast}%)`
  }

  function getEditedImageDataUrl() {
    return new Promise((resolve, reject) => {
      const imgSrc = processedImage || photo.url
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      const image = new Image()
      image.crossOrigin = 'anonymous'

      image.onload = () => {
        if (cropMode && cropArea.width < 100) {
          const scaleX = image.width / 100
          const scaleY = image.height / 100
          canvas.width = cropArea.width * scaleX
          canvas.height = cropArea.height * scaleY
          ctx.filter = getImageFilter()
          ctx.drawImage(image, cropArea.x * scaleX, cropArea.y * scaleY, canvas.width, canvas.height, 0, 0, canvas.width, canvas.height)
        } else {
          canvas.width = image.width
          canvas.height = image.height
          ctx.filter = getImageFilter()
          ctx.drawImage(image, 0, 0)
        }
        resolve(processedImage ? canvas.toDataURL('image/png') : canvas.toDataURL('image/jpeg', 0.9))
      }

      image.onerror = () => reject(new Error('Failed to load image'))
      image.src = imgSrc
    })
  }

  // --- Handlers ---

  function handleAutoLevel() {
    setImageAdjustments({ brightness: 105, contrast: 110, autoApplied: true })
  }

  function handleBrightnessChange(value) {
    setImageAdjustments(prev => ({ ...prev, brightness: value, autoApplied: false }))
  }

  function handleContrastChange(value) {
    setImageAdjustments(prev => ({ ...prev, contrast: value, autoApplied: false }))
  }

  function handleDiscardProcessedImage() {
    setProcessedImage(null)
    setBgSource(null)
  }

  async function handleRemoveBg() {
    if (bgAlreadyUsed) {
      showModalToast('Due to API costs, you should not re-run background removal. If absolutely necessary, please reupload and try again.')
      return
    }

    setRemovingBg(true)
    try {
      const response = await api.post('/api/image/remove-background', {
        image_url: photo.url,
        model: bgModel,
        alpha_matting: true,
        alpha_matting_foreground_threshold: 270,
        alpha_matting_background_threshold: 20,
        alpha_matting_erode_size: 10,
        post_process_mask: true
      })
      if (response.success && response.image_data) {
        setProcessedImage(`data:${response.content_type};base64,${response.image_data}`)
        setBgSource(response.source || null)
        if (response.credits_remaining !== undefined) setBgCredits(response.credits_remaining)

        // Log usage in database (update local state even if logging fails)
        if (onBgRemovalUsed) onBgRemovalUsed(photo.photography_photo_id)
        try {
          await api.post(`/api/warehouse/photography/${itemId}/photos/${photo.photography_photo_id}/bg-removal-used`, {
            source: response.source || 'unknown'
          })
        } catch (logErr) {
          console.error('Failed to log bg removal usage:', logErr)
        }
      } else {
        throw new Error('No image data returned')
      }
    } catch (err) {
      console.error('Background removal failed:', err)
      showModalToast('Background removal failed. Please try again.')
    } finally {
      setRemovingBg(false)
    }
  }

  async function handleMake4x5Ratio() {
    const imgSrc = processedImage || photo.url
    setMakingRatio(true)
    try {
      await new Promise((resolve, reject) => {
        const image = new Image()
        image.crossOrigin = 'anonymous'
        image.onload = () => {
          const targetRatio = 4 / 5
          const currentRatio = image.width / image.height
          let newWidth, newHeight

          if (currentRatio > targetRatio) {
            newWidth = image.width
            newHeight = Math.round(image.width / targetRatio)
          } else if (currentRatio < targetRatio) {
            newHeight = image.height
            newWidth = Math.round(image.height * targetRatio)
          } else {
            resolve()
            return
          }

          const canvas = document.createElement('canvas')
          const ctx = canvas.getContext('2d')
          canvas.width = newWidth
          canvas.height = newHeight
          ctx.fillStyle = '#FFFFFF'
          ctx.fillRect(0, 0, newWidth, newHeight)
          ctx.filter = getImageFilter()
          ctx.drawImage(image, Math.round((newWidth - image.width) / 2), Math.round((newHeight - image.height) / 2))
          setProcessedImage(canvas.toDataURL('image/png'))
          resolve()
        }
        image.onerror = () => reject(new Error('Failed to load image'))
        image.src = imgSrc
      })
    } catch (err) {
      console.error('4:5 ratio conversion failed:', err)
    } finally {
      setMakingRatio(false)
    }
  }

  async function handleAutoProcessAll() {
    if (bgAlreadyUsed) {
      showModalToast('Due to API costs, you should not re-run background removal. If absolutely necessary, please reupload and try again.')
      return
    }

    setAutoProcessing(true)
    try {
      setAutoProcessStep('Applying colour correction...')
      const adjustments = { brightness: 105, contrast: 110, autoApplied: true }
      setImageAdjustments(adjustments)
      const filterStr = `brightness(${adjustments.brightness}%) contrast(${adjustments.contrast}%)`

      setAutoProcessStep('Removing background...')
      const response = await api.post('/api/image/remove-background', {
        image_url: photo.url,
        model: bgModel,
        alpha_matting: true,
        alpha_matting_foreground_threshold: 270,
        alpha_matting_background_threshold: 20,
        alpha_matting_erode_size: 10,
        post_process_mask: true
      })
      if (!response.success || !response.image_data) throw new Error('Background removal failed')
      const bgRemovedImage = `data:${response.content_type};base64,${response.image_data}`
      setBgSource(response.source || null)
      if (response.credits_remaining !== undefined) setBgCredits(response.credits_remaining)

      // Log usage in database (update local state even if logging fails)
      if (onBgRemovalUsed) onBgRemovalUsed(photo.photography_photo_id)
      try {
        await api.post(`/api/warehouse/photography/${itemId}/photos/${photo.photography_photo_id}/bg-removal-used`, {
          source: response.source || 'unknown'
        })
      } catch (logErr) {
        console.error('Failed to log bg removal usage:', logErr)
      }

      setAutoProcessStep('Applying 4:5 portrait ratio...')
      const finalImage = await new Promise((resolve, reject) => {
        const image = new Image()
        image.crossOrigin = 'anonymous'
        image.onload = () => {
          const targetRatio = 4 / 5
          const currentRatio = image.width / image.height
          let newWidth, newHeight

          if (currentRatio > targetRatio) {
            newWidth = image.width
            newHeight = Math.round(image.width / targetRatio)
          } else if (currentRatio < targetRatio) {
            newHeight = image.height
            newWidth = Math.round(image.height * targetRatio)
          } else {
            const c = document.createElement('canvas')
            const x = c.getContext('2d')
            c.width = image.width
            c.height = image.height
            x.filter = filterStr
            x.drawImage(image, 0, 0)
            resolve(c.toDataURL('image/png'))
            return
          }

          const canvas = document.createElement('canvas')
          const ctx = canvas.getContext('2d')
          canvas.width = newWidth
          canvas.height = newHeight
          ctx.fillStyle = '#FFFFFF'
          ctx.fillRect(0, 0, newWidth, newHeight)
          ctx.filter = filterStr
          ctx.drawImage(image, Math.round((newWidth - image.width) / 2), Math.round((newHeight - image.height) / 2))
          resolve(canvas.toDataURL('image/png'))
        }
        image.onerror = () => reject(new Error('Failed to load image'))
        image.src = bgRemovedImage
      })

      setProcessedImage(finalImage)
    } catch (err) {
      console.error('Auto process failed:', err)
      showModalToast('Auto processing failed. Please try individual steps.')
      resetImageAdjustments()
    } finally {
      setAutoProcessing(false)
      setAutoProcessStep('')
    }
  }

  async function handleToggleCrop() {
    if (cropMode) {
      if (cropArea.width < 100 || cropArea.height < 100) {
        try {
          const imgSrc = processedImage || photo.url
          await new Promise((resolve, reject) => {
            const image = new Image()
            image.crossOrigin = 'anonymous'
            image.onload = () => {
              const canvas = document.createElement('canvas')
              const ctx = canvas.getContext('2d')
              const scaleX = image.width / 100
              const scaleY = image.height / 100
              canvas.width = cropArea.width * scaleX
              canvas.height = cropArea.height * scaleY
              ctx.filter = getImageFilter()
              ctx.drawImage(image, cropArea.x * scaleX, cropArea.y * scaleY, canvas.width, canvas.height, 0, 0, canvas.width, canvas.height)
              setProcessedImage(canvas.toDataURL('image/png'))
              resolve()
            }
            image.onerror = () => reject(new Error('Failed to load image'))
            image.src = imgSrc
          })
        } catch (err) {
          console.error('Crop failed:', err)
        }
      }
      setCropMode(false)
      setCropArea({ x: 0, y: 0, width: 100, height: 100 })
    } else {
      setCropMode(true)
      setCropArea({ x: 10, y: 10, width: 80, height: 80 })
    }
  }

  function handleCropMouseDown(e) {
    if (!cropMode) return
    const rect = e.currentTarget.getBoundingClientRect()
    cropStartRef.current = {
      x: ((e.clientX - rect.left) / rect.width) * 100,
      y: ((e.clientY - rect.top) / rect.height) * 100
    }
  }

  function handleCropMouseMove(e) {
    if (!cropMode || !cropStartRef.current) return
    const rect = e.currentTarget.getBoundingClientRect()
    const x = ((e.clientX - rect.left) / rect.width) * 100
    const y = ((e.clientY - rect.top) / rect.height) * 100
    const startX = Math.min(cropStartRef.current.x, x)
    const startY = Math.min(cropStartRef.current.y, y)
    setCropArea({
      x: Math.max(0, startX),
      y: Math.max(0, startY),
      width: Math.min(100 - startX, Math.abs(x - cropStartRef.current.x)),
      height: Math.min(100 - startY, Math.abs(y - cropStartRef.current.y))
    })
  }

  function handleCropMouseUp() {
    cropStartRef.current = null
  }

  async function handleSave() {
    setSaving(true)
    try {
      const dataUrl = await getEditedImageDataUrl()
      const base64Data = dataUrl.split(',')[1]
      await api.post(`/api/warehouse/photography/${itemId}/photos/process`, {
        photoId: photo.photography_photo_id,
        processedImageData: base64Data
      })
      onProcessed()
      handleClose()
    } catch (err) {
      console.error('Save failed:', err)
    } finally {
      setSaving(false)
    }
  }

  async function handleDownloadImage() {
    try {
      const dataUrl = await getEditedImageDataUrl()
      const link = document.createElement('a')
      link.download = `${photo.filename || 'photo'}-edited.png`
      link.href = dataUrl
      link.click()
    } catch (err) {
      console.error('Failed to download image:', err)
    }
  }

  const anyProcessing = autoProcessing || removingBg || makingRatio
  const hasEdits = processedImage || imageAdjustments.brightness !== 100 || imageAdjustments.contrast !== 100

  return (
    <div className="lightbox-overlay" onClick={handleClose}>
      <div className="lightbox-content lightbox-with-controls" onClick={e => e.stopPropagation()}>
        <button className="lightbox-close" onClick={handleClose}>&times;</button>

        <div
          className="lightbox-image-container"
          onMouseDown={handleCropMouseDown}
          onMouseMove={handleCropMouseMove}
          onMouseUp={handleCropMouseUp}
          onMouseLeave={handleCropMouseUp}
        >
          <img
            ref={imageRef}
            src={processedImage || photo.url}
            alt={photo.filename}
            className="lightbox-image"
            style={{ filter: getImageFilter(), background: processedImage ? '#f0f0f0' : 'transparent' }}
            draggable={false}
          />
          {cropMode && (
            <div className="crop-overlay">
              <div
                className="crop-selection"
                style={{
                  left: `${cropArea.x}%`,
                  top: `${cropArea.y}%`,
                  width: `${cropArea.width}%`,
                  height: `${cropArea.height}%`
                }}
              />
            </div>
          )}
        </div>

        {/* Controls — matches inventory lightbox exactly */}
        <div className="lightbox-controls">
          {modalToast && (
            <div style={{
              padding: '8px 14px',
              marginBottom: '8px',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: 500,
              background: modalToast.type === 'error' ? '#fceeed' : '#e8f5ee',
              color: modalToast.type === 'error' ? '#E5625E' : '#3BB273',
              border: `1px solid ${modalToast.type === 'error' ? '#E5625E' : '#3BB273'}40`
            }}>
              {modalToast.message}
            </div>
          )}
          <div className="lightbox-controls-row">
            <button
              className="btn btn-sm btn-primary"
              onClick={handleAutoProcessAll}
              disabled={anyProcessing}
              style={bgAlreadyUsed ? { opacity: 0.5 } : undefined}
              title={bgAlreadyUsed ? 'BG removal already used on this photo' : 'Auto Level + Remove BG + 4:5 Portrait'}
            >
              {autoProcessing ? autoProcessStep || 'Processing...' : bgAlreadyUsed ? 'Auto Process (BG used)' : 'Auto Process All'}
            </button>
            <button className="btn btn-sm btn-secondary" onClick={handleAutoLevel} disabled={anyProcessing}>
              Auto Level
            </button>
            <button
              className={`btn btn-sm ${cropMode ? 'btn-accent' : 'btn-secondary'}`}
              onClick={handleToggleCrop}
              disabled={anyProcessing}
            >
              {cropMode ? 'Apply Crop' : 'Crop'}
            </button>
            <button
              className={`btn btn-sm ${processedImage ? 'btn-accent' : 'btn-secondary'}`}
              onClick={processedImage ? handleDiscardProcessedImage : handleRemoveBg}
              disabled={anyProcessing}
              style={bgAlreadyUsed && !processedImage ? { opacity: 0.5 } : undefined}
              title={bgAlreadyUsed && !processedImage ? 'BG removal already used — reupload to try again' : undefined}
            >
              {removingBg ? 'Processing...' : processedImage ? 'Undo BG' : bgAlreadyUsed ? 'BG Used ✓' : 'Remove BG'}
            </button>
            <button
              className="btn btn-sm btn-secondary"
              onClick={handleMake4x5Ratio}
              disabled={anyProcessing}
              title="Add whitespace for 4:5 portrait ratio"
            >
              {makingRatio ? 'Processing...' : '4:5 Portrait'}
            </button>
            <button className="btn btn-sm btn-secondary" onClick={resetImageAdjustments} disabled={anyProcessing}>
              Reset
            </button>
          </div>
          <div className="lightbox-controls-row">
            <label className="lightbox-slider-label">
              Brightness: {imageAdjustments.brightness}%
              <input
                type="range" min="50" max="150"
                value={imageAdjustments.brightness}
                onChange={e => handleBrightnessChange(parseInt(e.target.value))}
                className="lightbox-slider"
              />
            </label>
            <label className="lightbox-slider-label">
              Contrast: {imageAdjustments.contrast}%
              <input
                type="range" min="50" max="150"
                value={imageAdjustments.contrast}
                onChange={e => handleContrastChange(parseInt(e.target.value))}
                className="lightbox-slider"
              />
            </label>
            <label className="lightbox-slider-label">
              BG Model:
              <select
                value={bgModel}
                onChange={e => setBgModel(e.target.value)}
                style={{ marginLeft: 4, padding: '2px 4px', fontSize: '0.8rem', borderRadius: 'var(--radius-sm)', border: '1px solid var(--color-border)', background: '#1a1a2e', color: '#fff' }}
              >
                <option value="isnet-general-use">ISNet (best)</option>
                <option value="u2net">U2Net</option>
                <option value="u2netp">U2Net Lite (fast)</option>
                <option value="silueta">Silueta (small)</option>
              </select>
            </label>
          </div>
          <div className="lightbox-controls-row">
            <button className="btn btn-sm btn-secondary" onClick={handleDownloadImage}>
              Download
            </button>
            <button
              className="btn btn-sm btn-primary"
              onClick={handleSave}
              disabled={saving || !hasEdits}
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
          {cropMode && (
            <div className="lightbox-crop-hint">Drag on image to select crop area</div>
          )}
          {processedImage && (
            <div className="lightbox-crop-hint" style={{ color: '#4ade80' }}>
              Image modified{bgSource ? ` (via ${bgSource})` : ''}{bgCredits !== null ? ` — ${bgCredits} credits left` : ''} — Click "Save" to keep changes
            </div>
          )}
        </div>
      </div>
    </div>
  )
}


// =============================================
// QueueView — lists pending photography items
// =============================================

function QueueView({ items, loading, onStartItem, onRefresh, isAdmin }) {
  const [addingDummy, setAddingDummy] = useState(false)

  async function handleAddDummy() {
    setAddingDummy(true)
    try {
      await api.post('/api/warehouse/photography/queue', {
        source: 'testing',
        productName: 'TEST \u2014 Dummy Photography Item',
        productType: 'Test Equipment',
        brand: 'Test Brand',
        sellPrice: 99.99,
        buyPrice: 49.99,
        quantity: 1,
        overallCondition: 'Excellent'
      })
      onRefresh()
    } catch (error) {
      console.error('Failed to add dummy item:', error)
    } finally {
      setAddingDummy(false)
    }
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
        <Spinner size={28} />
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: theme.colors.charcoal }}>
            Photography Queue
          </h2>
          <Badge color="blue">{items.length} items</Badge>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {isAdmin && (
            <Button variant="secondary" size="sm" onClick={handleAddDummy} disabled={addingDummy}>
              {addingDummy ? 'Adding...' : '+ Dummy Item'}
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={onRefresh}>Refresh</Button>
        </div>
      </div>

      {/* Empty state */}
      {items.length === 0 && (
        <Card>
          <div style={{ textAlign: 'center', padding: '48px 0', color: theme.colors.grey500 }}>
            <div style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>No Items for Photography</div>
            <div style={{ fontSize: '14px' }}>All items have been photographed.</div>
          </div>
        </Card>
      )}

      {/* Queue table */}
      {items.length > 0 && (
        <Card padding={false}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${theme.colors.grey200}` }}>
                  {['Trade ID', 'Item', 'Source', 'Brand', 'Product Type', 'Queued', 'Status', 'Actions'].map(h => (
                    <th key={h} style={{
                      padding: '12px 16px', textAlign: 'left',
                      fontSize: '12px', fontWeight: 600, textTransform: 'uppercase',
                      color: theme.colors.grey500, letterSpacing: '0.5px'
                    }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map(item => (
                  <QueueRow key={item.photography_queue_id} item={item} onStart={onStartItem} />
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  )
}

function QueueRow({ item, onStart }) {
  const statusColor = STATUS_BADGE_COLORS[item.status] || 'grey'
  const statusLabel = STATUS_LABELS[item.status] || item.status
  const isInProgress = item.status === 'in_progress'

  return (
    <tr style={{ borderBottom: `1px solid ${theme.colors.grey100}` }}>
      <td style={{ padding: '12px 16px', color: theme.colors.grey500 }}>
        {item.trade_id || '-'}
      </td>
      <td style={{ padding: '12px 16px', fontWeight: 500, color: theme.colors.charcoal }}>
        {item.product_name}
      </td>
      <td style={{ padding: '12px 16px' }}>
        <SourceBadge source={item.source} />
      </td>
      <td style={{ padding: '12px 16px', color: theme.colors.grey500 }}>
        {item.brand || '-'}
      </td>
      <td style={{ padding: '12px 16px', color: theme.colors.grey500 }}>
        {item.product_type || '-'}
      </td>
      <td style={{ padding: '12px 16px', color: theme.colors.grey500 }}>
        {formatTimeAgo(item.created_at)}
      </td>
      <td style={{ padding: '12px 16px' }}>
        <Badge color={statusColor}>{statusLabel}</Badge>
      </td>
      <td style={{ padding: '12px 16px' }}>
        <Button size="sm" variant={isInProgress ? 'primary' : 'success'} onClick={() => onStart(item)}>
          {isInProgress ? 'Continue' : 'Start'}
        </Button>
      </td>
    </tr>
  )
}


// =============================================
// PhotoEditor — upload, sort, process, complete
// =============================================

function PhotoEditor({ item, onBack, onComplete }) {
  const [photos, setPhotos] = useState([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0, pct: 0 })
  const [toast, setToast] = useState(null)
  const [processing, setProcessing] = useState(false)
  const [processProgress, setProcessProgress] = useState({ current: 0, total: 0 })
  const [completing, setCompleting] = useState(false)
  const [previewPhoto, setPreviewPhoto] = useState(null)
  const [dragOverIndex, setDragOverIndex] = useState(null)
  const [draggingIndex, setDraggingIndex] = useState(null)
  const [isDragOverZone, setIsDragOverZone] = useState(false)
  const fileInputRef = useRef(null)

  const itemId = item.photography_queue_id

  useEffect(() => {
    loadPhotos()
    markInProgress()
  }, [itemId])

  async function loadPhotos() {
    setLoading(true)
    try {
      const response = await api.get(`/api/warehouse/photography/${itemId}`)
      setPhotos(response.data?.photos || response.photos || [])
    } catch (error) {
      console.error('Failed to load photos:', error)
    } finally {
      setLoading(false)
    }
  }

  async function markInProgress() {
    try {
      await api.patch(`/api/warehouse/photography/${itemId}`, { status: 'in_progress' })
    } catch (error) {
      console.error('Failed to update status:', error)
    }
  }

  // --- Upload ---

  function showToast(message, type = 'error') {
    setToast({ message, type })
    const lineCount = message.split('\n').length
    setTimeout(() => setToast(null), Math.max(3000, lineCount * 2000))
  }

  async function handleFiles(files) {
    if (!files || files.length === 0) return

    const existingNames = new Set(photos.map(p => p.filename))
    const duplicates = files.filter(f => existingNames.has(f.name))
    const newFiles = files.filter(f => !existingNames.has(f.name))

    if (duplicates.length > 0) {
      const names = duplicates.map(f => f.name).join(', ')
      showToast(`Duplicate photo${duplicates.length > 1 ? 's' : ''} skipped: ${names}`)
    }

    if (newFiles.length === 0) return

    // Validate all files before uploading any
    const validFiles = []
    const rejections = []
    for (const file of newFiles) {
      const formatError = await validateImageFile(file)
      if (formatError) {
        rejections.push(formatError)
        continue
      }
      const qualityError = await checkImageQuality(file)
      if (qualityError) {
        rejections.push(qualityError)
        continue
      }
      validFiles.push(file)
    }

    if (rejections.length > 0) {
      showToast(rejections.join('\n'))
    }

    if (validFiles.length === 0) return

    setUploading(true)
    setUploadProgress({ current: 0, total: validFiles.length, pct: 0 })
    try {
      for (let i = 0; i < validFiles.length; i++) {
        const file = validFiles[i]
        const { base64, contentType } = await fileToBase64(file)
        const body = JSON.stringify({ imageData: base64, filename: file.name, contentType })

        await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest()
          xhr.open('POST', `/api/warehouse/photography/${itemId}/photos`)
          xhr.setRequestHeader('Content-Type', 'application/json')
          xhr.withCredentials = true
          xhr.upload.onprogress = (e) => {
            if (e.lengthComputable) {
              const filePct = e.loaded / e.total
              const overallPct = Math.round(((i + filePct) / validFiles.length) * 100)
              setUploadProgress({ current: i, total: validFiles.length, pct: overallPct })
            }
          }
          xhr.onload = () => xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Upload failed: ${xhr.status}`))
          xhr.onerror = () => reject(new Error('Upload failed'))
          xhr.send(body)
        })
      }
      setUploadProgress({ current: validFiles.length, total: validFiles.length, pct: 100 })
      await loadPhotos()
    } catch (error) {
      console.error('Failed to upload photos:', error)
    } finally {
      setUploading(false)
      setUploadProgress({ current: 0, total: 0, pct: 0 })
    }
  }

  function handleFileInput(e) {
    handleFiles(Array.from(e.target.files))
    e.target.value = ''
  }

  function handleDropOnZone(e) {
    e.preventDefault()
    setIsDragOverZone(false)
    const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'))
    handleFiles(files)
  }

  function handleDragOverZone(e) {
    e.preventDefault()
    setIsDragOverZone(true)
  }

  function handleDragLeaveZone() {
    setIsDragOverZone(false)
  }

  // --- Delete ---

  async function handleDeletePhoto(photoId) {
    try {
      await api.delete(`/api/warehouse/photography/${itemId}/photos/${photoId}`)
      await loadPhotos()
    } catch (error) {
      console.error('Failed to delete photo:', error)
    }
  }

  // --- Reorder (drag & drop within grid) ---

  function handleDragStart(e, index) {
    setDraggingIndex(index)
    e.dataTransfer.effectAllowed = 'move'
  }

  function handleDragEnd() {
    setDraggingIndex(null)
    setDragOverIndex(null)
  }

  function handleDragOver(e, index) {
    e.preventDefault()
    setDragOverIndex(index)
  }

  async function handleDropOnThumb(e, dropIndex) {
    e.preventDefault()
    const startIndex = draggingIndex
    setDragOverIndex(null)
    setDraggingIndex(null)
    if (startIndex === null || startIndex === dropIndex) return

    const reordered = [...photos]
    const [moved] = reordered.splice(startIndex, 1)
    reordered.splice(dropIndex, 0, moved)
    setPhotos(reordered)

    try {
      const photoIds = reordered.map(p => p.photography_photo_id)
      await api.patch(`/api/warehouse/photography/${itemId}/photos/reorder`, { photoIds })
    } catch (error) {
      console.error('Failed to reorder photos:', error)
      await loadPhotos()
    }
  }

  // --- Process all ---

  async function handleProcessAll() {
    const unprocessed = photos.filter(p => !p.is_processed)
    if (unprocessed.length === 0) {
      showToast('All photos are already processed')
      return
    }

    setProcessing(true)
    setProcessProgress({ current: 0, total: unprocessed.length })

    try {
      console.log('[ProcessAll] Starting. Photos:', JSON.stringify(photos.map(p => ({
        id: p.photography_photo_id, is_processed: p.is_processed, bg_removal_used: p.bg_removal_used, url: p.url?.slice(0, 80)
      }))))

      let processed = 0
      for (let i = 0; i < photos.length; i++) {
        const photo = photos[i]
        console.log(`[ProcessAll] Photo ${i}: is_processed=${photo.is_processed}, bg_removal_used=${photo.bg_removal_used}`)

        if (photo.is_processed) {
          console.log(`[ProcessAll] Photo ${i}: already processed, skipping`)
          continue
        }

        setProcessProgress({ current: processed, total: unprocessed.length })

        const isLead = i === 0
        let processedBase64

        if (isLead && !photo.bg_removal_used) {
          console.log(`[ProcessAll] Photo ${i}: removing background...`)
          const bgResult = await removeBackground(photo.url)
          console.log(`[ProcessAll] Photo ${i}: applying colour+ratio...`)
          processedBase64 = await applyColourAndRatio(bgResult.image_data, bgResult.content_type)

          // Log bg removal usage (update local state even if logging fails)
          setPhotos(prev => prev.map(p =>
            p.photography_photo_id === photo.photography_photo_id ? { ...p, bg_removal_used: 1 } : p
          ))
          try {
            await api.post(`/api/warehouse/photography/${itemId}/photos/${photo.photography_photo_id}/bg-removal-used`, {
              source: 'batch-process'
            })
          } catch (logErr) {
            console.error('Failed to log bg removal usage:', logErr)
          }
        } else {
          console.log(`[ProcessAll] Photo ${i}: fetching via proxy...`)
          const { base64, contentType } = await urlToBase64(`/api/warehouse/photography/photos/${photo.photography_photo_id}/raw`)
          console.log(`[ProcessAll] Photo ${i}: applying colour+ratio (${Math.round(base64.length / 1024)}KB base64)...`)
          processedBase64 = await applyColourAndRatio(base64, contentType)
        }

        console.log(`[ProcessAll] Photo ${i}: uploading processed image (${Math.round(processedBase64.length / 1024)}KB)...`)
        await api.post(`/api/warehouse/photography/${itemId}/photos/process`, {
          photoId: photo.photography_photo_id,
          processedImageData: processedBase64
        })
        processed++
        console.log(`[ProcessAll] Photo ${i}: done (${processed}/${unprocessed.length})`)
      }

      setProcessProgress({ current: unprocessed.length, total: unprocessed.length })
      await loadPhotos()
      showToast(`Processed ${unprocessed.length} photo${unprocessed.length > 1 ? 's' : ''}`)
    } catch (error) {
      console.error('Failed to process photos:', error)
      showToast(`Processing failed: ${error.message}`)
    } finally {
      setProcessing(false)
    }
  }

  // --- Complete ---

  async function handleComplete() {
    setCompleting(true)
    try {
      await api.post(`/api/warehouse/photography/${itemId}/complete`)
      onComplete()
    } catch (error) {
      console.error('Failed to complete photography:', error)
    } finally {
      setCompleting(false)
    }
  }

  const allProcessed = photos.length > 0 && photos.every(p => p.is_processed)
  const processedCount = photos.filter(p => p.is_processed).length

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
        <Spinner size={28} />
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* Back button + title */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <Button variant="ghost" size="sm" onClick={onBack}>Back to Queue</Button>
        <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: theme.colors.charcoal }}>
          Photo Editor
        </h2>
      </div>

      {/* Item info bar */}
      <InfoBar item={item} />

      {/* Upload zone */}
      <UploadZone
        isDragOver={isDragOverZone}
        uploading={uploading}
        uploadProgress={uploadProgress}
        onDrop={handleDropOnZone}
        onDragOver={handleDragOverZone}
        onDragLeave={handleDragLeaveZone}
        onClickBrowse={() => fileInputRef.current?.click()}
      />
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*"
        onChange={handleFileInput}
        style={{ display: 'none' }}
      />

      {/* Photo grid */}
      {photos.length > 0 && (
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <div style={{ fontSize: '16px', fontWeight: 600, color: theme.colors.charcoal }}>
              Photos ({photos.length})
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {photos.length > 1 && (
                <span style={{ fontSize: '12px', color: theme.colors.grey400 }}>
                  Drag to reorder
                </span>
              )}
              <span style={{ fontSize: '13px', color: theme.colors.grey500 }}>
                {processedCount}/{photos.length} processed
              </span>
            </div>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
            gap: '12px'
          }}>
            {photos.map((photo, index) => (
              <PhotoThumbnail
                key={photo.photography_photo_id}
                photo={photo}
                index={index}
                isLead={index === 0}
                isDropTarget={dragOverIndex === index && draggingIndex !== index}
                isDragging={draggingIndex === index}
                isAnyDragging={draggingIndex !== null}
                onDragStart={(e) => handleDragStart(e, index)}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => handleDragOver(e, index)}
                onDrop={(e) => handleDropOnThumb(e, index)}
                onPreview={() => setPreviewPhoto({ ...photo, isLead: index === 0 })}
                onDelete={() => handleDeletePhoto(photo.photography_photo_id)}
              />
            ))}
          </div>
        </Card>
      )}

      {/* Processing + completion controls */}
      {photos.length > 0 && (
        <Card>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {processing && (
              <ProgressBar
                current={processProgress.current}
                total={processProgress.total}
                label="Processing photos"
              />
            )}

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <Button
                variant="primary"
                onClick={handleProcessAll}
                disabled={processing || photos.length === 0}
              >
                {processing ? 'Processing...' : 'Process All'}
              </Button>
              <Button
                variant="success"
                onClick={handleComplete}
                disabled={!allProcessed || completing}
              >
                {completing ? 'Creating Draft...' : 'Complete & Create Draft'}
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Photo edit modal */}
      <PhotoEditModal
        photo={previewPhoto}
        itemId={itemId}
        onClose={() => setPreviewPhoto(null)}
        onProcessed={loadPhotos}
        onBgRemovalUsed={(photoId) => {
          setPhotos(prev => prev.map(p =>
            p.photography_photo_id === photoId ? { ...p, bg_removal_used: 1 } : p
          ))
          if (previewPhoto?.photography_photo_id === photoId) {
            setPreviewPhoto(prev => prev ? { ...prev, bg_removal_used: 1 } : prev)
          }
        }}
      />

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: '24px', left: '50%', transform: 'translateX(-50%)',
          padding: '12px 24px', borderRadius: theme.radius.md,
          background: toast.type === 'error' ? theme.colors.crimson : theme.colors.greenOnions,
          color: theme.colors.white, fontSize: '0.9em', fontWeight: 500,
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)', zIndex: 9999,
          maxWidth: '500px', textAlign: 'left', whiteSpace: 'pre-line'
        }}>
          {toast.message}
        </div>
      )}
    </div>
  )
}


// --- PhotoEditor sub-components ---

function InfoBar({ item }) {
  return (
    <Card>
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: '24px',
        fontSize: '14px', color: theme.colors.charcoal
      }}>
        <InfoField label="Brand" value={item.brand || '-'} />
        <InfoField label="Product Type" value={item.product_type || '-'} />
        <InfoField label="Trade ID" value={item.trade_id || '-'} />
        <InfoField label="Condition" value={item.overall_condition || '-'} />
        <InfoField label="Source" value={<SourceBadge source={item.source} />} />
        <InfoField label="Sell Price" value={formatCurrency(item.sell_price)} />
      </div>
    </Card>
  )
}

function InfoField({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: '12px', color: theme.colors.grey500, fontWeight: 600, marginBottom: '2px' }}>
        {label}
      </div>
      <div style={{ fontWeight: 500 }}>{value}</div>
    </div>
  )
}

function UploadZone({ isDragOver, uploading, uploadProgress, onDrop, onDragOver, onDragLeave, onClickBrowse }) {
  const pct = uploadProgress.pct || 0

  return (
    <Card>
      <div
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        style={{
          border: `2px dashed ${isDragOver ? theme.colors.sunburst : theme.colors.grey300}`,
          borderRadius: theme.radius.md,
          padding: '40px',
          textAlign: 'center',
          background: isDragOver ? theme.colors.amberLight : theme.colors.grey100,
          transition: 'all 0.2s ease',
          cursor: uploading ? 'default' : 'pointer'
        }}
        onClick={uploading ? undefined : onClickBrowse}
      >
        {uploading ? (
          <div style={{ maxWidth: '300px', margin: '0 auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '13px' }}>
              <span style={{ color: theme.colors.charcoal, fontWeight: 600 }}>
                {uploadProgress.total > 1
                  ? `Uploading ${uploadProgress.current + 1} of ${uploadProgress.total}...`
                  : 'Uploading...'}
              </span>
              <span style={{ color: theme.colors.grey500 }}>{pct}%</span>
            </div>
            <div style={{
              height: '8px', borderRadius: '4px',
              background: theme.colors.grey200, overflow: 'hidden'
            }}>
              <div style={{
                height: '100%', borderRadius: '4px',
                background: theme.colors.sunburst,
                width: `${pct}%`,
                transition: 'width 0.2s ease'
              }} />
            </div>
          </div>
        ) : (
          <>
            <div style={{ fontSize: '16px', fontWeight: 600, color: theme.colors.charcoal, marginBottom: '4px' }}>
              Drop photos here or click to browse
            </div>
            <div style={{ fontSize: '13px', color: theme.colors.grey500 }}>
              Accepts JPG, PNG, WebP
            </div>
          </>
        )}
      </div>
    </Card>
  )
}

const PhotoThumbnail = memo(function PhotoThumbnail({
  photo, index, isLead, isDropTarget, isDragging, isAnyDragging,
  onDragStart, onDragEnd, onDragOver, onDrop,
  onPreview, onDelete
}) {
  const imageUrl = photo.processedUrl || photo.url

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      onDrop={onDrop}
      style={{
        position: 'relative',
        borderRadius: theme.radius.md,
        overflow: 'hidden',
        border: `1px solid ${theme.colors.grey200}`,
        background: theme.colors.grey100,
        cursor: 'grab',
        aspectRatio: '4/5',
        opacity: isDragging ? 0.3 : 1,
        boxShadow: isDropTarget
          ? `0 0 0 3px ${theme.colors.sunburst}, ${theme.shadows.md}`
          : isDragging
            ? `0 0 0 3px ${theme.colors.imBlue}`
            : 'none',
        transform: isDropTarget ? 'scale(1.05)' : 'scale(1)',
        transition: 'transform 30ms ease, opacity 30ms ease, box-shadow 30ms ease'
      }}
    >
      {/* Image */}
      <img
        src={imageUrl}
        alt={photo.filename}
        onClick={onPreview}
        style={{
          width: '100%', height: '100%',
          objectFit: 'cover', display: 'block',
          cursor: 'pointer',
          pointerEvents: isAnyDragging ? 'none' : 'auto'
        }}
      />

      {/* Position badge */}
      <span style={{
        position: 'absolute', top: '6px', left: '6px',
        padding: '2px 8px', borderRadius: '12px',
        fontSize: '11px', fontWeight: 700,
        background: isLead ? theme.colors.sunburst : theme.colors.white,
        color: theme.colors.charcoal,
        boxShadow: theme.shadows.sm,
        pointerEvents: 'none'
      }}>
        {isLead ? 'Lead' : `#${index + 1}`}
      </span>

      {/* Processed checkmark */}
      {photo.is_processed && (
        <span style={{
          position: 'absolute', top: '6px', right: '6px',
          width: '22px', height: '22px', borderRadius: '50%',
          background: theme.colors.greenOnions, color: theme.colors.white,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '13px', fontWeight: 700,
          pointerEvents: 'none'
        }}>
          &#10003;
        </span>
      )}

      {/* Drag grip indicator */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0,
        padding: '8px 0 5px',
        display: 'flex', justifyContent: 'center', gap: '3px',
        background: 'linear-gradient(transparent, rgba(0,0,0,0.45))',
        pointerEvents: 'none'
      }}>
        <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: 'rgba(255,255,255,0.9)' }} />
        <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: 'rgba(255,255,255,0.9)' }} />
        <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: 'rgba(255,255,255,0.9)' }} />
      </div>

      {/* Delete button */}
      <button
        onClick={(e) => { e.stopPropagation(); onDelete() }}
        style={{
          position: 'absolute', bottom: '6px', right: '6px',
          width: '24px', height: '24px', borderRadius: '50%',
          background: theme.colors.crimson, color: theme.colors.white,
          border: 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '14px', fontWeight: 700, zIndex: 1
        }}
      >
        x
      </button>
    </div>
  )
})


// =============================================
// Root Photography component
// =============================================

function Photography() {
  const { user } = useAuth()
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeItem, setActiveItem] = useState(null)

  useEffect(() => {
    loadItems()
  }, [])

  async function loadItems() {
    setLoading(true)
    try {
      const response = await api.get('/api/warehouse/photography')
      setItems(response.data || response.items || [])
    } catch (error) {
      console.error('Failed to load photography items:', error)
    } finally {
      setLoading(false)
    }
  }

  function handleStartItem(item) {
    setActiveItem(item)
  }

  function handleBack() {
    setActiveItem(null)
    loadItems()
  }

  function handleComplete() {
    setActiveItem(null)
    loadItems()
  }

  const isAdmin = Boolean(user?.is_admin)

  if (activeItem) {
    return (
      <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto', fontFamily: theme.fonts.sans }}>
        <PhotoEditor item={activeItem} onBack={handleBack} onComplete={handleComplete} />
      </div>
    )
  }

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto', fontFamily: theme.fonts.sans }}>
      <QueueView
        items={items}
        loading={loading}
        onStartItem={handleStartItem}
        onRefresh={loadItems}
        isAdmin={isAdmin}
      />
    </div>
  )
}

export default Photography
